import { useEffect, useMemo, useRef, useState } from 'react';
import { Jeton } from './Jeton.jsx';

const Ligne = (style) => <div style={{ position: 'absolute', border: '1.5px solid var(--pline)', ...style }} />;

/**
 * Le terrain et ses onze jetons. Notre but est en bas, sauf inversion.
 *
 * Les jetons sont placés en PIXELS, calculés à partir de la taille mesurée du terrain,
 * et non en pourcentages. La raison est concrète : en pourcentages, le gardien à
 * y=6 tombe à 94 % de la hauteur, si bien que son rond dépasse le bas du terrain et
 * que son nom, posé encore en dessous, est coupé net. La bande utile se calcule ici
 * en retirant le rayon d'un jeton en haut, et le rayon plus la hauteur d'étiquette en
 * bas — plus rien ne peut sortir, quelle que soit la taille de l'écran.
 *
 * La taille des jetons suit la hauteur disponible : un iPhone SE et un Pro Max n'ont
 * pas le même terrain, et un jeton fixe est soit énorme sur l'un, soit perdu sur l'autre.
 */
export function Terrain({ places, effectif, retourne, paysage = false, selection, cibles = [], evenements = {}, entrees = {}, onTapPlace, style, children }) {
  const cadre = useRef(null);
  const [mesure, setMesure] = useState({ l: 0, h: 0 });

  useEffect(() => {
    const el = cadre.current;
    if (!el) return;
    const observateur = new ResizeObserver(([e]) => {
      const r = e.contentRect;
      setMesure({ l: r.width, h: r.height });
    });
    observateur.observe(el);
    return () => observateur.disconnect();
  }, []);

  const par = new Map(effectif.map((j) => [j.id, j]));

  // LA TAILLE DES JETONS SE DÉDUIT DE LA FORMATION, pas seulement de la hauteur.
  //
  // Deux jetons proches en largeur et voisins en hauteur se chevauchent si leur écart
  // vertical ne tient pas le rond ET l'étiquette. C'est arrivé au gardien et à l'axial
  // central de toutes les défenses à trois et à cinq. Les coordonnées ont été
  // corrigées, mais s'en remettre à elles serait fragile : la prochaine formation
  // ajoutée ramènerait le défaut. On mesure donc le plus petit écart réellement
  // présent et on dimensionne pour lui — le chevauchement devient impossible, quelles
  // que soient les coordonnées.
  const ecartMin = useMemo(() => {
    let min = Infinity;
    for (let i = 0; i < places.length; i++) {
      for (let j = i + 1; j < places.length; j++) {
        if (Math.abs(places[i].x - places[j].x) > 16) continue;   // assez écartés en largeur
        min = Math.min(min, Math.abs(places[i].y - places[j].y));
      }
    }
    return Number.isFinite(min) ? min : 20;
  }, [places]);

  // Hauteur disponible pour cet écart, moins une respiration. Le facteur 1.45 tient le
  // rond plus son étiquette (0,42 × taille) plus l'interligne.
  const bande = Math.max(0, (paysage ? mesure.l : mesure.h) - 26);
  const plafond = ((ecartMin / 100) * bande - 8) / 1.45;
  const taille = Math.max(26, Math.min(44, Math.round(Math.min((paysage ? mesure.h : mesure.h) / 8.5, plafond))));
  const etiquette = Math.round(taille * 0.42);

  const hautMin = taille / 2 + 3;
  const hautMax = mesure.h - taille / 2 - etiquette - 4;
  const gaucheMin = taille / 2 + 2;
  const gaucheMax = mesure.l - taille / 2 - 2;

  const position = (p) => {
    const y = retourne ? 100 - p.y : p.y;
    // En PAYSAGE, les axes s'échangent : la profondeur du terrain court en largeur et
    // notre but est à gauche. C'est l'orientation d'un écran de télévision, et celle
    // qu'on attend quand on tourne le téléphone.
    if (paysage) {
      return {
        gauche: gaucheMin + (y / 100) * (gaucheMax - gaucheMin),
        haut: hautMin + (p.x / 100) * (hautMax - hautMin),
      };
    }
    return {
      // y se lit depuis NOTRE but ; en haut de l'écran c'est le camp adverse, donc le
      // gardien est en bas, comme sur toutes les feuilles de match.
      haut: hautMax - (y / 100) * (hautMax - hautMin),
      gauche: gaucheMin + (p.x / 100) * (gaucheMax - gaucheMin),
    };
  };

  return (
    <div
      ref={cadre}
      style={{
        position: 'relative', overflow: 'hidden',
        background: 'var(--pitch)', border: '1px solid var(--line)', borderRadius: 16,
        ...style,
      }}
    >
      {/* Les bandes de tonte donnent l'échelle du terrain, ce qu'un aplat ne fait pas. */}
      <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(0deg,rgba(255,255,255,.022) 0 44px,transparent 44px 88px)' }} />

      {Ligne({ left: '3%', right: '3%', top: '3%', bottom: '3%', borderRadius: 3 })}
      {paysage ? (
        <>
          <div style={{ position: 'absolute', top: '3%', bottom: '3%', left: '50%', width: 1.5, background: 'var(--pline)' }} />
          {Ligne({ left: '50%', top: '50%', height: '30%', aspectRatio: '1', transform: 'translate(-50%,-50%)', borderRadius: '50%' })}
          {Ligne({ left: '3%', top: '24%', bottom: '24%', width: '11%', borderLeft: 'none' })}
          {Ligne({ right: '3%', top: '24%', bottom: '24%', width: '11%', borderRight: 'none' })}
          {Ligne({ left: '3%', top: '38%', bottom: '38%', width: '4.5%', borderLeft: 'none' })}
          {Ligne({ right: '3%', top: '38%', bottom: '38%', width: '4.5%', borderRight: 'none' })}
        </>
      ) : (
        <>
          <div style={{ position: 'absolute', left: '3%', right: '3%', top: '50%', height: 1.5, background: 'var(--pline)' }} />
          {Ligne({ left: '50%', top: '50%', width: '26%', aspectRatio: '1', transform: 'translate(-50%,-50%)', borderRadius: '50%' })}
          {Ligne({ left: '22%', right: '22%', bottom: '3%', height: '12%', borderBottom: 'none' })}
          {Ligne({ left: '22%', right: '22%', top: '3%', height: '12%', borderTop: 'none' })}
          {Ligne({ left: '36%', right: '36%', bottom: '3%', height: '5%', borderBottom: 'none' })}
          {Ligne({ left: '36%', right: '36%', top: '3%', height: '5%', borderTop: 'none' })}
        </>
      )}

      {mesure.h > 0 && places.map((p) => {
        const { haut, gauche } = position(p);
        return (
          <Jeton
            key={p.index}
            joueur={par.get(p.joueurId) ?? null}
            code={p.code}
            gauche={gauche}
            haut={haut}
            taille={taille}
            joueurs={effectif}
            selection={selection === p.index}
            cible={cibles.includes(p.index)}
            evenements={evenements[p.joueurId] ?? []}
            entreA={entrees[p.joueurId]}
            onTap={() => onTapPlace?.(p.index)}
          />
        );
      })}

      {children}
    </div>
  );
}
